import { ReactElement, ReactNode, cloneElement } from 'react';
import { useAuth } from '@actschurch/ccms-sdk';

export interface AccessRule {
  roles?: string[];
  permissions?: string[];
  domains?: string[];
  requireAll?: boolean; // If true, requires ALL roles/permissions, not just any
}

/**
 * Check if user has access based on roles, permissions, and domains
 */
export function hasAccess(
  userRoles: string[] = [],
  userPermissions: string[] = [],
  userDomains: string[] = [],
  rule?: AccessRule
): boolean {
  if (!rule) return true;

  // Admin users have access to everything
  if (userRoles.includes('Admin') || userRoles.includes('admin')) {
    return true;
  }

  const { roles, permissions, domains, requireAll = false } = rule;

  // Check roles
  let matchesRole = true;
  if (roles && roles.length > 0) {
    if (requireAll) {
      matchesRole = roles.every((role) => userRoles.includes(role));
    } else {
      matchesRole = roles.some((role) => userRoles.includes(role));
    }
  }

  // Check permissions
  let matchesPermission = true;
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      matchesPermission = permissions.every((perm) => userPermissions.includes(perm));
    } else {
      matchesPermission = permissions.some((perm) => userPermissions.includes(perm));
    }
  }

  // Check domains
  let matchesDomain = true;
  if (domains && domains.length > 0) {
    if (requireAll) {
      matchesDomain = domains.every((domain) => userDomains.includes(domain));
    } else {
      matchesDomain = domains.some((domain) => userDomains.includes(domain));
    }
  }

  return matchesRole && matchesPermission && matchesDomain;
}

interface AccessControlProps extends AccessRule {
  children: ReactNode;
  fallback?: ReactNode;
  mode?: 'hide' | 'disable' | 'blur';
  disabledMessage?: string;
}

/**
 * AccessControl component for role/permission-based UI rendering
 *
 * @example
 * // Hide element if user doesn't have admin role
 * <AccessControl roles={['admin']}>
 *   <AdminPanel />
 * </AccessControl>
 *
 * @example
 * // Disable button if user doesn't have edit permission
 * <AccessControl permissions={['edit']} mode="disable">
 *   <Button>Edit</Button>
 * </AccessControl>
 *
 * @example
 * // Show fallback content
 * <AccessControl roles={['manager']} fallback={<UpgradePrompt />}>
 *   <ManagerDashboard />
 * </AccessControl>
 */
export function AccessControl({
  children,
  fallback = null,
  mode = 'hide',
  roles,
  permissions,
  domains,
  requireAll,
  disabledMessage = 'You do not have permission to perform this action',
}: AccessControlProps) {
  const { state } = useAuth();
  const user = state.user;
  // Map SDK user model: rights → permissions, no domains in SDK yet
  const allowed = hasAccess(
    user?.roles ?? [],
    user?.rights ?? [],
    (user as any)?.domains || [],
    { roles, permissions, domains, requireAll }
  );

  // User has access - render children normally
  if (allowed) {
    return <>{children}</>;
  }

  // Handle different modes for unauthorized access
  switch (mode) {
    case 'disable':
      // Clone element and add disabled prop
      if (children && typeof children === 'object' && 'props' in (children as object)) {
        const element = children as ReactElement;
        return cloneElement(element, {
          disabled: true,
          'data-disabled-reason': 'insufficient-permissions',
          title: disabledMessage,
          style: {
            ...((element.props as Record<string, unknown>)?.style || {}),
            cursor: 'not-allowed',
            opacity: 0.6,
          },
        });
      }
      return <>{fallback}</>;

    case 'blur':
      // Blur the content with an overlay
      return (
        <div
          style={{
            position: 'relative',
            filter: 'blur(4px)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          data-access-restricted="true"
        >
          {children}
        </div>
      );

    case 'hide':
    default:
      // Completely hide the element
      return <>{fallback}</>;
  }
}

/**
 * Hook to check if user has access to a resource
 *
 * @example
 * const canEdit = useCanAccess({ permissions: ['edit'] });
 * const isAdmin = useCanAccess({ roles: ['admin'] });
 */
export function useCanAccess(rule?: AccessRule): boolean {
  const { state } = useAuth();
  const user = state.user;
  // Map SDK user model: rights → permissions
  return hasAccess(
    user?.roles ?? [],
    user?.rights ?? [],
    (user as any)?.domains || [],
    rule
  );
}

/**
 * Hook to get access control helpers
 *
 * @example
 * const { hasRole, hasPermission, hasDomain, canAccess } = useAccessControl();
 */
export function useAccessControl() {
  const { state } = useAuth();
  const user = state.user;

  // Map SDK user model: rights → permissions
  const permissions = user?.rights ?? [];
  const domains = (user as any)?.domains || [];
  const userRoles = user?.roles ?? [];

  return {
    hasRole: (role: string) => userRoles.includes(role),
    hasAnyRole: (roles: string[]) => roles.some((r) => userRoles.includes(r)),
    hasAllRoles: (roles: string[]) => roles.every((r) => userRoles.includes(r)),

    hasPermission: (permission: string) => permissions.includes(permission),
    hasAnyPermission: (perms: string[]) => perms.some((p) => permissions.includes(p)),
    hasAllPermissions: (perms: string[]) => perms.every((p) => permissions.includes(p)),

    hasDomain: (domain: string) => domains.includes(domain),
    hasAnyDomain: (doms: string[]) => doms.some((d) => domains.includes(d)),
    hasAllDomains: (doms: string[]) => doms.every((d) => domains.includes(d)),

    canAccess: (rule: AccessRule) => hasAccess(userRoles, permissions, domains, rule),

    isAdmin: () => userRoles.includes('admin'),
    isSuperAdmin: () => userRoles.includes('super_admin'),
  };
}

/**
 * Higher-order component for access control
 *
 * @example
 * const AdminOnlyComponent = withAccessControl(MyComponent, { roles: ['admin'] });
 */
export function withAccessControl<P extends object>(
  Component: React.ComponentType<P>,
  rule: AccessRule,
  FallbackComponent?: React.ComponentType
) {
  return function AccessControlledComponent(props: P) {
    const allowed = useCanAccess(rule);

    if (!allowed) {
      return FallbackComponent ? <FallbackComponent /> : null;
    }

    return <Component {...props} />;
  };
}
